import { css } from '@emotion/react';
import { Box } from '@spark-web/box';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Text } from '@spark-web/text';
import { TextLink } from '@spark-web/text-link';
import { FC, ReactNode, useEffect, useState } from 'react';

import { BrighteLogo } from '~/components/common/brighte-logo';
import { ASSET_URL, DEFAULT_LOGO_HEIGHT } from '~/constants';
import { useActiveChannel } from '~/context/PartnerConfigContext';
import { useIsomorphicLayoutEffect } from '~/hooks/useIsomorphicLayoutEffect';
import { useStepContext } from '~/provider/StepProvider';
import { sendParentEvent } from '~/utils/premarketplace';
import { getFinancingInterestContent } from '~/utils/premarketplace/financing-interest';

import { ProgressBar } from './components/progress-bar';

interface PremarketplaceDialogProps {
  children: ReactNode;
}

const FinancingInterestLogo = ({ provider, alt }: { provider: string; alt: string }) => {
  if (provider === 'brighte') {
    return <BrighteLogo height={DEFAULT_LOGO_HEIGHT} width={130} />;
  }

  return <img src={`${ASSET_URL}/partners/${provider}-logo.svg`} alt={alt} height={DEFAULT_LOGO_HEIGHT} width={130} />;
};

const DialogTitle = () => {
  const { currentStepId } = useStepContext();
  const activeChannel = useActiveChannel();

  if (currentStepId !== 'financing-interest') {
    return <ProgressBar />;
  }

  const content = getFinancingInterestContent(activeChannel?.preMarketplace?.stepConfiguration);

  // The Brighte logo already appears in the step content on this screen, so the header
  // logo would be redundant — only non-Brighte partners (e.g. CommBank) need it here.
  if (content.headerLogo.provider === 'brighte') {
    return <ProgressBar />;
  }

  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="medium" width="full">
      <FinancingInterestLogo provider={content.headerLogo.provider} alt={content.headerLogo.alt} />
      <Box flex={1}>
        <ProgressBar />
      </Box>
    </Box>
  );
};

export const PremarketplaceDialog: FC<PremarketplaceDialogProps> = ({ children }) => {
  const { currentStepId } = useStepContext();
  const [showCloseButton, setShowCloseButton] = useState(false);

  /**
   * Server renders with isOpen=false so the Radix Dialog portal is absent from
   * the SSR HTML. useIsomorphicLayoutEffect fires synchronously on the client
   * before the first paint, opening the dialog without a visible flash.
   * On the server it falls back to useEffect (a no-op during SSR), suppressing
   * React's "useLayoutEffect does nothing on the server" warning.
   */
  const [isOpen, setIsOpen] = useState(false);
  useIsomorphicLayoutEffect(() => {
    setIsOpen(true);
  }, []);

  useEffect(() => {
    try {
      setShowCloseButton(window?.top?.location.origin !== window?.location.origin);
    } catch {
      setShowCloseButton(true);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !showCloseButton) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, showCloseButton]);

  const onToggle = () => {
    if (isOpen) {
      sendParentEvent('track', { stepName: currentStepId, action: 'closed' });
      if (showCloseButton) setIsOpen(false);
    } else {
      sendParentEvent('track', { stepName: currentStepId, action: 'opened' });
      setIsOpen(true);
    }
  };

  return (
    <ContentDialog
      title={<DialogTitle />}
      description={currentStepId}
      isOpen={isOpen}
      onToggle={onToggle}
      size="large"
      padding={{ desktop: 'xxxlarge', mobile: 'small' }}
      showCloseButton={showCloseButton}
      scrollable
      css={css`
        position: fixed !important;
        display: block !important;
        inset: 0;
        & > :first-child {
          height: auto;
          max-width: 600px;
          max-height: none;
          margin: auto;
        }
      `}
    >
      {children}
      <Box
        gap="xxxlarge"
        alignSelf="center"
        display="flex"
        flexDirection="row"
        position="absolute"
        css={css({
          bottom: -35,
        })}
      >
        <Text tone="neutralInverted">
          <TextLink href="https://brighte.com.au/agreement" target="_blank" rel="noopener noreferrer">
            Terms
          </TextLink>
        </Text>
        <Text tone="neutralInverted">
          <TextLink href="https://brighte.com.au/privacy" target="_blank" rel="noopener noreferrer">
            Privacy policy
          </TextLink>
        </Text>
      </Box>
    </ContentDialog>
  );
};
